import React from 'react';
import { Box } from '@mui/material';
import { Button, Chip, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { formatScanDate, getNetworkScanChipColors, getNetworkScanStateLabel } from '../../config/networkScanConfig';
import NetworkScanGuideLink from '../NetworkScanGuide/NetworkScanGuideLink';
import { buildNetworkScanDetailUrl } from '../../utils/networkScanNavigation';
import { getInstalledBaseNodeTypeName } from '../../utils/getInstalledBaseNodeTypeName';
import { EffectiveNetworkScanRef, NetworkScanSourceRef } from '../../utils/iotAttachGate';
import { useNetworkScanUserName } from '../../hooks/useNetworkScanUserName';

export interface NetworkScanGateReasonProps {
    /** Effective scan that blocks (ORANGE / RED), or null when nothing is documented. */
    scan?: EffectiveNetworkScanRef | null;
    /** Installed base node of the asset / system — jump-off target when nothing is documented. */
    ownNodeId?: string | null;
    /** Ancestor ids root-first (see useIotAttachGate). */
    pathNodeIds?: string[];
    align?: 'center' | 'start';
    /** `inline` = one wrapping row for the hero actions bar; `stack` (default) for full-page / modal reasons. */
    variant?: 'stack' | 'inline';
}

const humanizeTypeKey = (key: string): string => {
    const humanized = key.replace(/_/g, ' ').toLowerCase();
    return humanized.charAt(0).toUpperCase() + humanized.slice(1);
};

/** "Documented on Plant Dresden" — type (from its key) plus name; the type alone when the node has no title. */
export const describeNetworkScanSource = (
    source: NetworkScanSourceRef | null | undefined,
    t: TFunction,
): string => {
    if (!source) return '';
    // The source carries only the type KEY; the type name is unknown here, so the
    // key is shown (humanised) unless it is the client root.
    const typeName = source.nodeTypeKey
        ? getInstalledBaseNodeTypeName({ key: source.nodeTypeKey, name: humanizeTypeKey(source.nodeTypeKey) } as any, t)
        : '';
    return source.name ? (typeName ? `${typeName} ${source.name}` : source.name) : typeName;
};

/**
 * Explains why a new IoT connection cannot be attached because of the network
 * scan (IBM-1039): the effective state, where and by whom it is documented, the guide and
 * a jump into the installed base tree with the "Network scan" tab open. Shared
 * by the asset connection wizard, the system registration modal and the
 * system dashboard. The source is plain text on purpose — the user may not be
 * allowed to open that node.
 */
const NetworkScanGateReason = ({ scan, ownNodeId, pathNodeIds = [], align = 'center', variant = 'stack' }: NetworkScanGateReasonProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const colors = getNetworkScanChipColors(scan?.state);
    const sourceLabel = describeNetworkScanSource(scan?.source, t);
    const documentedBy = useNetworkScanUserName(scan?.source?.installedBaseNodeId);
    const target = buildNetworkScanDetailUrl(ownNodeId, pathNodeIds, scan?.source?.installedBaseNodeId);
    const centered = align === 'center' && variant !== 'inline';
    const inline = variant === 'inline';

    return (
        <Box
            data-testid="network-scan-reason"
            sx={{
                display: 'flex',
                flexDirection: inline ? 'row' : 'column',
                flexWrap: inline ? 'wrap' : 'nowrap',
                alignItems: inline ? 'center' : centered ? 'center' : 'flex-start',
                textAlign: centered ? 'center' : 'left',
                gap: inline ? '12px' : '10px',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: centered ? 'center' : 'flex-start' }}>
                <Typography level="body-md">{t('Network scan')}:</Typography>
                <Chip
                    size="sm"
                    variant="soft"
                    data-testid="network-scan-reason-state"
                    sx={{ backgroundColor: colors.bg, color: colors.text, fontWeight: 600 }}
                >
                    {getNetworkScanStateLabel(scan?.state, t)}
                </Chip>
                {scan?.scanDate && (
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        {formatScanDate(scan.scanDate)}
                    </Typography>
                )}
            </Box>

            {sourceLabel && (
                <Typography
                    level="body-sm"
                    sx={{ color: 'text.secondary' }}
                    data-testid={documentedBy ? 'network-scan-reason-documented-by' : undefined}
                >
                    {documentedBy
                        ? t('Documented on {{name}}, {{date}}, by {{user}}', { name: sourceLabel, date: formatScanDate(scan?.scanDate), user: documentedBy })
                        : t('Documented on {{name}}, {{date}}', { name: sourceLabel, date: formatScanDate(scan?.scanDate) })}
                </Typography>
            )}

            <NetworkScanGuideLink />

            <Button
                variant="outlined"
                color="neutral"
                size="sm"
                data-testid="network-scan-goto-ib"
                onClick={() => navigate({ pathname: target.pathname, search: target.search })}
            >
                {t('Go to Installed Base')}
            </Button>
        </Box>
    );
};

export default NetworkScanGateReason;
